import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  ArrowRight,
  Pen,
  Trash2,
  LockKeyhole,
  Info,
  X,
  Pencil,
  Loader2,
} from "lucide-react";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "./ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { CheckboxRound } from "./ui/checkboxRound";
import Header from "../layouts/Header";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../hooks/use-toast";
import logoSmall from "../assets/images/logoSmallGrey.svg";
import infoIcon2 from "../assets/icons/info_icon.svg";
import padlockIcon from "../assets/icons/padlock.svg";
import { Button } from "./ui/button";
import CreateSubAccount from "./CreateSubAccount";
import { Separator } from "./ui/separator";

import {
  closeSubAccount,
  createSubAccount,
  setAccount,
  transferFunds,
  updateAccount,
} from "../services/settingsService";

import TransferFunds from "./TransferFunds";
import ConfirmTransfer from "./ConfirmTransfer";
import { useSubAccounts } from "../hooks/query/queries";
import { Skeleton } from "./ui/skeleton";
import { useInvalidateAll } from "../services/invalidator";
import UpdateSubAccount from "./UpdateSubAccount";
import CheckFundsBeforeDelete from "./CheckFundsBeforeDelete";
import {
  findUserActivities,
  transferMultipleAccountFunds,
} from "../services/findUserActivites";
import AccountDeleteBlock from "./AccountDeleteBlock";
import { fa } from "zod/v4/locales";
export default function SubAccounts() {
  const [open, setOpen] = useState(false);
  const [openUpdate, setOpenUpdate] = useState(false);
  const [subAccountName, setSubAccountName] = useState(null);
  const [subAccountNameUpdate, setSubAccountNameUpdate] = useState(null);
  const [accSelected, setAccSelected] = useState(true);
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [transfer, setTransfer] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [finalLoading, setFinalLoading] = useState(false);
  const [loadingAcc, setLoadingAcc] = useState(false);
  const [childAmount, setChildAmount] = useState(null);
  const [openConfirm, setOpenConfirm] = useState(null);
  const [openBlock, setOpenBlock] = useState(null);
  const { toast } = useToast();
  const invalidateAll = useInvalidateAll();
  const { userProfile, settings, updateSettings, updateProfile } = useAuth();
  const userId = userProfile?._id;
  const [transferDataPartial, setTransferDataPartial] = useState(null);
  const [assetBalances, setAssetBalances] = useState(null);
  const [activeInvestments, setActiveInvestments] = useState(null);
  const [activeSettlements, setActiveSettlements] = useState(null);
  const [activeOrders, setActiveOrders] = useState(null);
  const [loadingDelete, setLoadingDelete] = useState(null);
  const [accountEstimate, setAccountEstimate] = useState(null);
  const [userActivities, setUserActivities] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [mainAccount, setMainAccount] = useState(null);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const handleAccountName = (e) => {
    const name = e.target.value;
    setSubAccountName(name);
  };
  const handleEdit = (account) => {
    setAccSelected(account);
    setSubAccountNameUpdate(account?.accountName);
    setOpenUpdate(true);
  };
  const userActivies = async (account) => {
    if (userProfile?.accountId == account?._id) {
      toast({
        description: "Cannot proceed. Please change your default account",
        variant: "eighty",
      });
      return;
    }
    setLoadingDelete(account?._id);
    setSelectedAccount(account);
    const activities = await findUserActivities(account);
    setUserActivities(activities);
    if (activities?.hasActiveInvestments) {
      setActiveInvestments(activities?.activeInvestments);
    }
    if (activities?.hasPendingOrders) {
      setActiveSettlements(activities?.pendingOrders);
    }
    if (activities?.hasPendingSettlements) {
      setActiveOrders(activities?.pendingSettlements);
    }
    if (
      activities?.hasPendingSettlements ||
      activities?.hasPendingOrders ||
      activities?.hasActiveInvestments
    ) {
      setOpenBlock(true);
      setLoadingDelete(null);
    }
    if (
      !activities?.hasPendingSettlements &&
      !activities?.hasPendingOrders &&
      !activities?.hasActiveInvestments
    ) {
      checkAvailableAccountBalance(account);
    }
  };
  const checkAvailableAccountBalance = (account) => {
    const portfolioEstimate = account?.balance?.portfolioEstimateRaw;
    setAccountEstimate(portfolioEstimate);
    const accountAssetBalances = account?.balance?.assetBalances;
    setAssetBalances(accountAssetBalances);
    setOpenConfirm(true);
  };

  const transferMultiple = async (transfers, account) => {
    setLoading(true);
    try {
      const tranferMany = await transferMultipleAccountFunds(transfers);
      if (tranferMany) {
        console.log("tranfers", tranferMany);
        if (transfers) {
          toast({
            description: "Funds Tranferred successfully",
            variant: "eighty",
          });
        }
        handleDelete(account);
      }
    } catch (error) {
      console.log("transfer Error:", error);
    }
  };

  const handleDelete = async (account) => {
    try {
      const deletion = await closeSubAccount(account?.userId, account?._id);
      if (deletion) {
        queryClient.invalidateQueries({
          queryKey: ["subAccounts", userId],
          exact: false,
        });
      }
      return deletion;
    } catch (error) {
      console.log("error", error);
    } finally {
      setLoading(false);
      window.location.reload();
    }
  };
  const {
    data: accounts,
    isLoading,
    isError: accountError,
  } = useSubAccounts(userId);

  useEffect(() => {
    if (!accounts) return;

    const mainAccount = accounts.find((account) => account.isMainAccount);
    if (mainAccount) {
      setMainAccount(mainAccount);
    }
  }, [accounts]);
  const subAccountObj = {
    userId: userId,
    accountName: subAccountName,
  };
  const onCreate = async () => {
    setLoading(true);

    try {
      const response = await createSubAccount(subAccountObj);
      if (response) {
        queryClient.invalidateQueries({
          queryKey: ["subAccounts", userId],
          exact: false,
        });

        return response;
      }
    } catch (error) {
      console.log("Error Sub Accounts", error);
    } finally {
      setLoading(false);
      setOpen(false);
      window.location.reload();
    }
  };
  const onUpdate = async () => {
    setLoading(true);
    try {
      const response = await updateAccount(
        userId,
        accSelected?._id,
        subAccountNameUpdate
      );
      if (response) {
        queryClient.invalidateQueries({
          queryKey: ["subAccounts", userId],
          exact: false,
        });
        window.location.reload();
        return response;
      }
    } catch (error) {
      console.log("Error Sub Accounts", error);
    } finally {
      setLoading(false);
      setOpenUpdate(false);
    }
  };
  const onFunds = () => {
    setTransfer(true);
  };
  const handleFinish = async () => {
    setFinalLoading(true);
    const transfer = {
      userId: userId,
      accountIdFrom: transferDataPartial?.accountIdFrom,
      accountIdTo: transferDataPartial?.accountIdTo,
      amount: transferDataPartial?.amount,
      assetId: transferDataPartial?.assetId,
    };
    try {
      const response = await transferFunds(transfer);
      if (response) {
        setTransfer(false);
        setConfirm(false);
      }
      return response;
    } catch (error) {
      console.log("Transfer Error", error);
    } finally {
      setFinalLoading(false);
      window.location.reload();
    }
  };
  const handleConfirm = (data) => {
    setTransferDataPartial(data);
    setTransfer(false);
    setConfirm(true);
  };
  useEffect(() => {
    if (userProfile?.accountId && !selectedAccountId) {
      setSelectedAccountId(userProfile.accountId);
    }
  }, [userProfile, selectedAccountId]);

  const handleAccountChange = async (account) => {
    setLoadingAcc(true);
    try {
      setSelectedAccountId(account?._id);
      const res = await setAccount(account?.userId, account?._id);
      const data = res?.data?.data;
      updateProfile(data);
      toast({
        description: "Account updated successfully",
        variant: "eighty",
      });
      invalidateAll();
    } catch (error) {
      console.error("Error setting account:", error.message);
    } finally {
      setLoadingAcc(false);
      window.location.reload();
    }
  };
  const slipObject = {
    accountIdFrom: transferDataPartial?.accountIdFrom,
    accountIdFromName: transferDataPartial?.accountIdFromName,
    accountIdTo: transferDataPartial?.accountIdTo,
    accountIdToName: transferDataPartial?.accountIdToName,
    amount: transferDataPartial?.amount,
    assetId: transferDataPartial?.selectedAssetId,
    asset: transferDataPartial?.asset,
    type: transferDataPartial?.type,
  };
  return (
    <>
      {!transfer && !confirm && (
        <div className="min-h-screen w-full bg-gray-100 flex flex-col">
          {/* Top Gradient Bar */}
          <div className="h-14 bg-gradient-to-r from-blue-600 to-purple-600 flex items-center pl-20 text-white">
            <div
              className="mr-5 border-rounded rounded-full p-1 border"
              onClick={() => {
                navigate("/settings/index");
              }}
            >
              <ArrowLeft size={14} />
            </div>
            <p className="text-sm font-medium">
              Settings / Sub accounts {transfer && "/ Transfer funds"}
            </p>
          </div>
          <div className="flex justify-center mt-12 px-4 md:px-16 lg:px-32">
            <div className="max-w-3xl w-full">
              <Card className="bg-white rounded-2xl shadow-sm overflow-hidden">
                <CardHeader className="px-9 pt-6 pb-4">
                  <CardTitle className="text-2xl font-semibold text-[#6D6584] text-[20px]">
                    <div className="flex items-center">
                      <div className="flex items-center gap-3.5">
                        Sub Accounts{" "}
                        <Info size={14} className="text-rose-500" />
                      </div>
                    </div>
                  </CardTitle>
                  <div>
                    <p className="text-gray-500 text-sm pr-10">
                      Sub-accounts let you organise your crypto holdings by
                      purpose—whether for trading, saving, or investing—making
                      it easier to track performance and manage your funds
                      effectively
                    </p>
                  </div>
                </CardHeader>

                <CardContent className="px-9 pt-2  pb-6">
                  {isLoading || loadingAcc || loading
                    ? Array(3)
                        .fill(0)
                        .map((_, index) => (
                          <div className="border rounded-lg mt-4 px-4 py-3 flex items-center justify-between bg-white min-w-[220px]">
                            <div className="flex items-center gap-2">
                              <div className="w-7 h-7 rounded-full border-2" />
                              <div className="flex flex-col leading-tight gap-1">
                                <Skeleton className="h-4 w-32 rounded-sm" />
                                <Skeleton className="h-3 w-24 rounded-sm" />
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Skeleton className="w-10 h-10 rounded-full" />
                              <Skeleton className="w-10 h-10 rounded-full" />
                            </div>
                          </div>
                        ))
                    : accounts
                        ?.sort(
                          (a, b) =>
                            (b.isMainAccount === true) -
                            (a.isMainAccount === true)
                        )
                        .map((account) => (
                          <div
                            key={account._id}
                            className={`border rounded-lg mt-4 px-4 py-3 flex items-center justify-between cursor-pointer bg-white min-w-[220px]`}
                          >
                            <div className="flex items-center gap-2">
                              <label className="flex items-center cursor-pointer">
                                <input
                                  type="radio"
                                  value={account?._id}
                                  checked={selectedAccountId === account?._id}
                                  onChange={() => handleAccountChange(account)}
                                  className="w-7 h-7 accent-rose-500 border-rose-600 cursor-pointer"
                                />
                              </label>
                              <div className="flex flex-col leading-tight">
                                <span className="text-sm font-bold text-gray-800">
                                  {account?.accountName}
                                </span>
                                <span className="text-xs text-gray-500">
                                  Available:{" "}
                                  {account?.balance?.portfolioEstimate}
                                </span>
                              </div>
                            </div>

                            <div className="flex items-center">
                              {account?.isMainAccount ? (
                                <div className="py-2"></div>
                              ) : (
                                <div className="flex justify-between gap-2">
                                  <Button
                                    variant="ghost"
                                    onClick={() => handleEdit(account)}
                                    className="w-10 h-10 text-gray-400 hover:text-gray-600 rounded-full p-0 flex items-center justify-center border border-gray-200 hover:bg-gray-100"
                                  >
                                    <Pencil
                                      size={18}
                                      className="text-gray-700"
                                    />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    onClick={() => userActivies(account)}
                                    className="w-10 h-10 text-gray-400 hover:text-gray-600 rounded-full p-0 flex items-center justify-center border border-gray-200 hover:bg-gray-100"
                                  >
                                    {loadingDelete === account._id ? (
                                      <Loader2 className="animate-spin w-5 h-5" />
                                    ) : (
                                      <Trash2
                                        size={18}
                                        className="text-gray-700"
                                      />
                                    )}
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                  <div className="flex justify-center py-3 mt-4">
                    <CreateSubAccount
                      open={open}
                      accounts={accounts}
                      loading={loading}
                      setOpen={setOpen}
                      onCreate={onCreate}
                      setSubAccountName={setSubAccountName}
                      subAccountName={subAccountName}
                      onFunds={onFunds}
                    />
                  </div>
                  <div className="flex justify-center">
                    <UpdateSubAccount
                      openUpdate={openUpdate}
                      loading={loading}
                      setOpenUpdate={setOpenUpdate}
                      onUpdate={onUpdate}
                      setSubAccountName={setSubAccountNameUpdate}
                      subAccountName={subAccountNameUpdate}
                      onFunds={onFunds}
                    />
                  </div>
                  <div className="flex justify-center">
                    <CheckFundsBeforeDelete
                      open={openConfirm}
                      accounts={accounts}
                      loading={loading}
                      setOpen={setOpenConfirm}
                      onCreate={onCreate}
                      accountEstimate={accountEstimate}
                      onDelete={transferMultiple}
                      account={selectedAccount}
                      mainAccount={mainAccount}
                      assetBalances={assetBalances}
                      subAccountName={subAccountName}
                      onFunds={onFunds}
                    />
                  </div>
                  <div className="flex justify-center">
                    <AccountDeleteBlock
                      open={openBlock}
                      accounts={accounts}
                      loading={loading}
                      activities={userActivities}
                      setOpen={setOpenBlock}
                      onCreate={onCreate}
                      assetBalances={assetBalances}
                      subAccountName={subAccountName}
                      onFunds={onFunds}
                    />
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      )}
      {transfer && (
        <div className="bg-gray-100">
          <TransferFunds
            setTransfer={setTransfer}
            onContinue={handleConfirm}
            accounts={accounts}
            setChildAmount={setChildAmount}
            childAmount={childAmount}
          />
        </div>
      )}
      {confirm && (
        <div className="bg-gray-100">
          <ConfirmTransfer
            finalLoading={finalLoading}
            onDone={handleFinish}
            setConfirm={setConfirm}
            transferObject={slipObject}
            setTransfer={setTransfer}
          />
        </div>
      )}
    </>
  );
}
